import { useCallback, useEffect, useRef, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { useForm, UseFormReturnType } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { navigate, useParams } from '@redwoodjs/router'
import { useMutation } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  TURNSTILE_INTERACTION_MESSAGE,
  useTurnstileToken,
  UseTurnstileTokenResult,
} from 'src/components/Turnstile/useTurnstileToken'
import { CREATE_BASIC_APPLICANT } from 'src/graphql/applicants.graphql'
import { JobListingType } from 'src/graphql/types/jobListings'
import { removeCountryCode, unformatPhoneNumber } from 'src/lib/phone.utils'
import { isValidPostalCode } from 'src/lib/zipLookup'

import { ShortFormJobListing } from './jobInfo.utils'
import {
  buildQuestionResponses,
  getAnswerLengthError,
  isQuestionAnswered,
  PublicTemplateQuestion,
  QuestionAnswers,
} from './shortFormQuestions.utils'

// A type alias, not an interface: zodResolver's validate signature takes
// Record<string, unknown>, which only an alias satisfies structurally.
export type ShortFormValues = {
  firstName: string
  middleName: string
  lastName: string
  email: string
  phone: string
  yearsOfExperience?: number
  zipCode: string
  termsAccepted: boolean
  isCommunicationOptIn: boolean
  questionAnswers: QuestionAnswers
}

export interface UseShortFormSubmitArgs {
  /** The listing being applied to — null while the query is in flight */
  jobListing: ShortFormJobListing | null
  /** Attribution token sent with the mutation, unchanged from the caller */
  trackingLink: string
  /** Resume dropzone state, owned by the caller (office positions only) */
  resumeFile: File[]
  /** Short-form template questions, already sorted by template drag order */
  publicQuestions: PublicTemplateQuestion[]
  /**
   * Fired once an office application succeeds, just before the confirmation
   * state flips — lets the caller tear down transient UI (a bottom sheet).
   */
  onSubmitted?: () => void
}

export interface UseShortFormSubmitResult {
  /** Spread onto ShortFormCard's `turnstile` prop so the card renders the
   *  Cloudflare widget next to its submit controls. */
  turnstile: UseTurnstileTokenResult
  form: UseFormReturnType<ShortFormValues>
  isOfficeJob: boolean
  loading: boolean
  handleSubmission: () => void
  /** Office applications end on the confirmation screen; drivers navigate away */
  isSubmitted: boolean
  applicationId?: string
}

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      // Remove the data:application/pdf;base64, prefix
      const base64 = (reader.result as string).split(',')[1]
      resolve(base64)
    }
    reader.onerror = (error) => reject(error)
  })
}

/**
 * Where a submitted driver application routes next. Skip the redundant SMS
 * login only when the active session is the identity the server will authorize
 * — it enforces application.phone === session phone. No session, a stale
 * session on a shared device, or an edited number all route through
 * /driver-login so the correct identity is verified first. appliedPhone and
 * sessionPhone are compared already normalized to national digits (see the
 * caller); redirectPhone is the full number the driver entered (E.164), carried
 * into the login URL so its field prefills with the right country code.
 */
export const resolvePostSubmitRoute = ({
  applicationId,
  appliedPhone,
  sessionPhone,
  redirectPhone,
}: {
  applicationId: string
  appliedPhone: string
  sessionPhone: string | null
  redirectPhone: string
}): string =>
  sessionPhone && appliedPhone === sessionPhone
    ? `/driver-application?applicationId=${applicationId}`
    : `/driver-login?redirectTo=/driver-application?applicationId=${applicationId}&phone=${encodeURIComponent(
        redirectPhone
      )}`

/**
 * The short form's validation, submission and post-submit routing, shared by
 * `/short-form/{trackingLink}` and the public job board so required fields,
 * error handling and the driver/office branch can't drift between them.
 */
export const useShortFormSubmit = ({
  jobListing,
  trackingLink,
  resumeFile,
  publicQuestions,
  onSubmitted,
}: UseShortFormSubmitArgs): UseShortFormSubmitResult => {
  const [applicant, setApplicant] = useState(null)
  const [isSubmitted, setIsSubmitted] = useState(false)

  // `?phone=` hint from the caller (e.g. the /c link) — used ONLY to pre-fill
  // the phone field, never to decide login. The skip decision below is gated on
  // the live session instead, so a stale session on a shared device can't skip.
  const { phone: verifiedPhone } = useParams()
  const { isAuthenticated, currentUser } = useAuth()

  // Proof a real browser rendered the form; verified by createBasicApplicant.
  const turnstile = useTurnstileToken('short-form')
  // A submit is in progress, including the wait for a Turnstile token before
  // the mutation starts. `useMutation`'s loading only flips once the request
  // is sent, so without this a second tap during the wait would reuse the
  // same single-use token and could create a duplicate applicant.
  const submittingRef = useRef(false)
  const [awaitingToken, setAwaitingToken] = useState(false)

  const isOfficeJob = jobListing?.type === JobListingType.OFFICE

  const schema = z.object({
    firstName: z
      .string()
      .min(2, { message: 'First name should have at least 2 letters' })
      .max(20, { message: 'First name should have at most 20 letters' }),
    lastName: z
      .string()
      .min(2, { message: 'Last name should have at least 2 letters' })
      .max(20, { message: 'Last name should have at most 20 letters' }),
    email: z.string().email({ message: 'Invalid email' }),
    phone: z
      .string()
      .min(1, { message: 'Phone number is required' })
      .refine(
        (data) => unformatPhoneNumber(removeCountryCode(data)).length === 10,
        {
          message:
            'Phone number should have 10 digits. This is not a condition of opt-in.',
        }
      ),
    yearsOfExperience: z
      .number({ required_error: 'Experience is required' })
      .multipleOf(0.1)
      .max(100, { message: 'Please enter accurate information' }),
    zipCode: isOfficeJob
      ? z.string()
      : z
          .string()
          .min(5, { message: 'Postal code must be at least 5 characters' })
          .max(10, { message: 'Postal code must be at most 10 characters' })
          .refine(isValidPostalCode, {
            message: 'Please enter a valid postal code',
          }),
    termsAccepted: z.literal(true, {
      errorMap: () => ({
        message: 'You must accept the Terms of Service and Privacy Policy',
      }),
    }),
    isCommunicationOptIn: z.boolean().optional(),
    // Custom question answers keyed by jobListingTemplateQuestionId —
    // required questions must be answered before submit, same resolver as
    // the fixed fields
    questionAnswers: z
      .record(z.union([z.string(), z.array(z.string()), z.undefined()]))
      .superRefine((answers: QuestionAnswers, ctx) => {
        publicQuestions.forEach((question) => {
          const answer = answers[question.jobListingTemplateQuestionId]
          if (question.isRequired && !isQuestionAnswered(question, answer)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [question.jobListingTemplateQuestionId],
              message: 'This question is required',
            })
          }
          // Mirror the server's 500/5000 text limits — otherwise oversized
          // answers pass client validation and die on the server with a
          // generic failure toast.
          const lengthError = getAnswerLengthError(question, answer)
          if (lengthError) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [question.jobListingTemplateQuestionId],
              message: lengthError,
            })
          }
        })
      }),
  })

  const form = useForm<ShortFormValues>({
    initialValues: {
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      phone: verifiedPhone || '',
      yearsOfExperience: undefined,
      zipCode: '',
      termsAccepted: false,
      isCommunicationOptIn: false,
      questionAnswers: {},
    },
    validate: zodResolver(schema),
  })

  const [addApplicant, { loading: loadingSubmitApplicant }] = useMutation(
    CREATE_BASIC_APPLICANT,
    {
      onCompleted: (data) => {
        turnstile.reset()
        const { applicantId, applicationId, segment } =
          data?.createBasicApplicant || {}
        if (segment) {
          datadogRum.setUserProperty('segment', segment.toLowerCase())
        }
        setApplicant({ applicantId, application: { applicationId } })
      },
      onError: (error) => {
        // Tokens are single-use: mint a fresh one so the retry can carry it.
        turnstile.reset()
        // Server-side validation messages are written client-safe — surface
        // them instead of a generic failure so the driver can correct input.
        toast(
          error.message || 'Short form failed, please try again later.',
          'error'
        )
      },
    }
  )

  useEffect(() => {
    if (!applicant) {
      return
    }
    // Office applications don't continue to the DOT application — keep the
    // confirmation screen. Driver applications skip it and go straight to
    // phone verification + the full DOT application.
    if (isOfficeJob) {
      onSubmitted?.()
      setIsSubmitted(true)
      return
    }
    const applicationId = applicant.application.applicationId
    const enteredPhone = unformatPhoneNumber(form.values.phone)
    const appliedPhone = removeCountryCode(enteredPhone)
    const sessionPhone =
      isAuthenticated && currentUser?.phone
        ? removeCountryCode(unformatPhoneNumber(currentUser.phone))
        : null

    navigate(
      resolvePostSubmitRoute({
        applicationId,
        appliedPhone,
        sessionPhone,
        redirectPhone: enteredPhone,
      }),
      { replace: true }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicant])

  useEffect(() => {
    if (form.values.isCommunicationOptIn) {
      form.clearFieldError('isCommunicationOptIn')
    }
  }, [form.values.isCommunicationOptIn, form])

  const submitApplicant = useCallback(
    async (values) => {
      if (submittingRef.current) return
      submittingRef.current = true
      try {
        await submitApplicantInner(values)
      } finally {
        setAwaitingToken(false)
        submittingRef.current = false
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trackingLink, resumeFile, isOfficeJob, publicQuestions]
  )

  // Everything after the guard lives here so any throw releases the guard
  // in submitApplicant's finally instead of leaving the form unsubmittable.
  const submitApplicantInner = async (values) => {
    let resumeBase64 = null
    let resumeFileName = null

    if (isOfficeJob && resumeFile.length > 0) {
      try {
        resumeBase64 = await fileToBase64(resumeFile[0])
        resumeFileName = resumeFile[0].name
      } catch (error) {
        console.error('Failed to convert file to base64:', error)
        toast('Failed to process resume file', 'error')
        return
      }
    }

    // One entry per answered question — unanswered optional questions are
    // omitted from the payload entirely
    const questionResponses = buildQuestionResponses(
      publicQuestions,
      values.questionAnswers ?? {}
    )

    const input = {
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      email: values.email,
      phone: unformatPhoneNumber(values.phone),
      yearsOfExperience: parseFloat(values.yearsOfExperience),
      zipCode: values.zipCode,
      trackingLink,
      resumeFileName,
      resumeBase64,
      isCommunicationOptIn: values.isCommunicationOptIn,
      ...(questionResponses.length > 0 ? { questionResponses } : {}),
    }
    // Waits if the widget is still verifying so a fast submit doesn't go out
    // tokenless; `{}` when Turnstile isn't configured.
    setAwaitingToken(true)
    const { token: turnstileToken, reason } = await turnstile.getToken()
    setAwaitingToken(false)
    if (reason === 'needs-interaction') {
      // The widget is now visible and running its check; the driver submits
      // again once it completes. Don't reset: that would discard the check.
      toast(TURNSTILE_INTERACTION_MESSAGE, 'error')
      return
    }
    // Resolves on success and on error (onError is set), so the caller's
    // guard clears once the request has settled.
    await addApplicant({
      variables: {
        input: turnstileToken
          ? { ...input, turnstileToken }
          : // Tokenless: say why, so the API log separates a slow widget from
          // a blocked script (a bot sends neither field).
          reason
          ? { ...input, turnstileClientReason: reason }
          : input,
      },
    })
  }

  const handleSubmission = () => {
    const validation = form.validate()

    if (isOfficeJob && resumeFile.length === 0) {
      toast('Resume is required for office positions', 'error')
      return
    }

    if (validation.hasErrors) {
      // Custom questions can sit below the fold (on desktop the Apply bar is
      // outside the card's scroll area) — bring the first errored question
      // into view. Deferred a frame so the error render commits the
      // data-error attribute first (same selector as CompanyQuestionsScreen).
      const firstErroredQuestion = publicQuestions.find(
        (question) =>
          validation.errors[
            `questionAnswers.${question.jobListingTemplateQuestionId}`
          ]
      )
      if (firstErroredQuestion) {
        requestAnimationFrame(() => {
          document
            .querySelector(
              `[data-question-id="${firstErroredQuestion.jobListingTemplateQuestionId}"]`
            )
            ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' })
        })
      }
      return
    }

    submitApplicant(form.values)
  }

  return {
    form,
    isOfficeJob,
    loading: loadingSubmitApplicant || awaitingToken,
    handleSubmission,
    isSubmitted,
    applicationId: applicant?.application?.applicationId,
    turnstile,
  }
}

export default useShortFormSubmit
